import { useMemo } from 'react';

import { DutyHourTypeDynFlag, DutyHourTypeFlag } from 'generated';
import { toInteger } from 'lodash';
import { useTranslation } from 'react-i18next';

import { dateFormatter } from 'components/shared/DataGrid/utils/valueFormatters/dateFormatter';
import { singleSelectValueFormatter } from 'components/shared/DataGrid/utils/valueFormatters/singleSelectValueFormatter';
import { isFieldInArrayValueGetter } from 'components/shared/DataGrid/utils/valueGetters/isFieldInArrayValueGetter';
import { nestedFieldValueGetter } from 'components/shared/DataGrid/utils/valueGetters/nestedFieldValueGetter';
import mapToOptions from 'utils/mapToOptions';

import { GridColumns, GridValidRowModel } from '@mui/x-data-grid-pro';

import { useGetDccOptions } from '../components/Sections/TimeBoundValuesSection/hooks/useGetDccOptions';
import ColorCell from 'components/shared/Table/TableCell/DataGrid/ColorCell';
import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';

export const useGetColumns = () => {
  const { t } = useTranslation('common');
  const { data: dccOptions, isFetching: isDccOptionsFetching } =
    useGetDccOptions();

  return useMemo<GridColumns<GridValidRowModel>>(
    () => [
      {
        headerName: t('dutyHourTypes.column.code'),
        field: 'code',
        type: 'string',
        hideable: false,
        minWidth: 100
      },
      {
        headerName: t('dutyHourTypes.column.id'),
        field: 'dutyTypeId',
        type: 'string',
        minWidth: 100
      },
      {
        headerName: t('dutyHourTypes.column.description'),
        field: 'description',
        flex: 1,
        type: 'string',
        minWidth: 260
      },
      {
        headerName: t('dutyHourTypes.column.dcc'),
        field: 'dcc.dccCode',
        type: 'singleSelect',
        flex: 1,
        minWidth: 260,
        filterable: !isDccOptionsFetching,
        valueOptions: mapToOptions(dccOptions ?? [], 'value', 'label'),
        valueGetter: (value) => toInteger(nestedFieldValueGetter(value)),
        valueFormatter: (params) =>
          singleSelectValueFormatter(params, dccOptions ?? [])
      },
      {
        headerName: t('validFrom'),
        field: 'valid.from',
        minWidth: 110,
        type: 'date',
        valueFormatter: dateFormatter,
        valueGetter: nestedFieldValueGetter
      },
      {
        headerName: t('validTo'),
        field: 'valid.to',
        minWidth: 110,
        type: 'date',
        valueFormatter: dateFormatter,
        valueGetter: nestedFieldValueGetter
      },
      {
        headerName: t('dutyHourTypes.column.color'),
        field: 'colorCode',
        align: 'center',
        minWidth: 50,
        renderCell: ({ value }) => <ColorCell value={value} />
      },
      {
        headerName: t('dutyHourTypes.column.default'),
        field: 'DEFAULT' as DutyHourTypeFlag,
        type: 'boolean',
        minWidth: 50,
        valueGetter: isFieldInArrayValueGetter('dutyTypeFlags'),
        renderCell: ({ value }) => <BooleanCell value={value} />
      },
      {
        headerName: t('dutyHourTypes.column.decentralized'),
        field: 'AVAILABLE_DECENTRALIZED' as DutyHourTypeDynFlag,
        type: 'boolean',
        minWidth: 50,
        valueGetter: isFieldInArrayValueGetter('flags'),
        renderCell: ({ value }) => <BooleanCell value={value} />
      }
    ],
    [dccOptions, isDccOptionsFetching]
  );
};
